import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Button, Skeleton, Alert, Chip, useMediaQuery, useTheme, Dialog, DialogContent, AppBar, Toolbar } from '@mui/material';
import { Icon } from '@iconify/react';
export interface EntityField {
  label: string;
  value: React.ReactNode;
  type?: 'text' | 'chip' | 'date' | 'email' | 'phone' | 'url';
  chipColor?: string;
  chipBgColor?: string;
}
export interface EntitySection {
  title: string;
  fields: EntityField[];
}
export interface EntityViewDrawerProps {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  title: string;
  subtitle?: string;
  entityId: string | number;
  sections: EntitySection[];
  isLoading?: boolean;
  error?: string;
  onRetry?: () => void;
}
const EntityViewDrawer: React.FC<EntityViewDrawerProps> = ({
  open,
  onClose,
  onEdit,
  title,
  subtitle,
  entityId,
  sections,
  isLoading = false,
  error,
  onRetry
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isOpen, setIsOpen] = useState(open);
  useEffect(() => {
    setIsOpen(open);
  }, [open]);
  const handleClose = () => {
    setIsOpen(false);
    setTimeout(onClose, 300); // Allow animation to complete
  };
  const renderFieldValue = (field: EntityField) => {
    if (field.type === 'chip') {
      return <Chip label={field.value} size="small" sx={{
        backgroundColor: field.chipBgColor || 'rgba(14, 165, 233, 0.1)',
        color: field.chipColor || '#0EA5E9',
        height: 24,
        fontWeight: 600
      }} data-id="element-2484" />;
    }
    if (field.type === 'date' && typeof field.value === 'string') {
      return new Date(field.value as string).toLocaleString();
    }
    return field.value;
  };
  const content = <>
      <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }} data-id="element-2485">
        {/* Header */}
        <Box sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }} data-id="element-2486">
          <Box data-id="element-2487">
            {isLoading ? <>
                <Skeleton width={180} height={32} data-id="element-2488" />
                {subtitle && <Skeleton width={120} height={24} data-id="element-2489" />}
              </> : <>
                <Typography variant="h5" component="h2" fontWeight={600} data-id="element-2490">
                  {title}
                </Typography>
                {subtitle && <Typography variant="body2" color="text.secondary" sx={{
              mt: 0.5
            }} data-id="element-2491">
                    {subtitle}
                  </Typography>}
              </>}
          </Box>
          <IconButton onClick={handleClose} aria-label="close" edge="end" sx={{
          ml: 2
        }} data-id="element-2492">
            <Icon icon="lucide:x" width={24} height={24} data-id="element-2493" />
          </IconButton>
        </Box>
        {/* Content */}
        <Box sx={{
        flexGrow: 1,
        overflowY: 'auto',
        p: 3
      }} data-id="element-2494">
          {error ? <Alert severity="error" action={onRetry && <Button color="inherit" size="small" onClick={onRetry} data-id="element-2496">
                    Retry
                  </Button>} sx={{
          mb: 2,
          borderRadius: 2
        }} data-id="element-2495">
              {error}
            </Alert> : isLoading ?
        // Loading skeletons
        Array.from({
          length: 3
        }).map((_, sectionIndex) => <Box key={`section-skeleton-${sectionIndex}`} sx={{
          mb: 4
        }} data-id="element-2497">
                <Skeleton width={150} height={32} sx={{
            mb: 2
          }} data-id="element-2498" />
                <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr'
            },
            gap: 3
          }} data-id="element-2499">
                  {Array.from({
              length: 4
            }).map((_, fieldIndex) => <Box key={`field-skeleton-${fieldIndex}`} data-id="element-2500">
                      <Skeleton width={100} height={20} sx={{
                mb: 1
              }} data-id="element-2501" />
                      <Skeleton width="100%" height={24} data-id="element-2502" />
                    </Box>)}
                </Box>
              </Box>) :
        // Actual content
        sections.map((section, sectionIndex) => <Box key={`section-${sectionIndex}`} sx={{
          mb: 4
        }} data-id="element-2503">
                <Typography variant="subtitle1" fontWeight={600} sx={{
            mb: 2,
            color: 'primary.main'
          }} data-id="element-2504">
                  {section.title}
                </Typography>
                <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr'
            },
            gap: 3
          }} data-id="element-2505">
                  {section.fields.map((field, fieldIndex) => <Box key={`field-${fieldIndex}`} data-id="element-2506">
                      <Typography variant="caption" color="text.secondary" sx={{
                mb: 0.5,
                display: 'block'
              }} data-id="element-2507">
                        {field.label}
                      </Typography>
                      <Typography variant="body1" data-id="element-2508">
                        {renderFieldValue(field)}
                      </Typography>
                    </Box>)}
                </Box>
              </Box>)}
        </Box>
        {/* Footer */}
        <Box sx={{
        p: 3,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-2509">
          <Button variant="outlined" color="inherit" onClick={handleClose} disabled={isLoading} data-id="element-2510">
            Close
          </Button>
          <Button variant="contained" onClick={onEdit} startIcon={<Icon icon="lucide:edit" width={18} height={18} data-id="element-2512" />} disabled={isLoading} data-id="element-2511">
            Edit
          </Button>
        </Box>
      </Box>
    </>;
  return isMobile ? <Dialog open={isOpen} onClose={handleClose} fullScreen TransitionProps={{
    onExited: onClose
  }} data-id="element-2513">
      <AppBar position="static" color="default" elevation={0} data-id="element-2514">
        <Toolbar data-id="element-2515">
          <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close" data-id="element-2516">
            <Icon icon="lucide:x" width={24} height={24} data-id="element-2517" />
          </IconButton>
          <Typography sx={{
          ml: 2,
          flex: 1
        }} variant="h6" component="div" noWrap data-id="element-2518">
            {isLoading ? <Skeleton width={150} data-id="element-2519" /> : title}
          </Typography>
          <Button autoFocus color="primary" onClick={onEdit} disabled={isLoading} data-id="element-2520">
            Edit
          </Button>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{
      p: 0
    }} data-id="element-2521">
        {content}
      </DialogContent>
    </Dialog> : <Drawer anchor="right" open={isOpen} onClose={handleClose} ModalProps={{
    keepMounted: true
  }} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: '480px'
      },
      boxShadow: '0 0 24px rgba(0,0,0,0.1)'
    }
  }} data-id="element-2522">
      {content}
    </Drawer>;
};
export default EntityViewDrawer;